import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { getDashboardViews, updateDashboardViews, type DashboardViewsDto } from '@/api/dashboard-views.api'
import { resetStore } from '@/store/actions/reset'
import type { Status } from '@/types/general-types'
import { getErrorMessage } from '@/utils/utils'

type UpdateDashboardViewsPayload = {
  profileId: string
  state: DashboardViewsDto
}

type DashboardViewsState = {
  data: DashboardViewsDto
  profileId: string | null
  loadRequestId: string | null
  loadStatus: Status
  saveStatus: Status
}

const createEmptyData = (): DashboardViewsDto => ({ views: [], activeViewId: 'default' })

const initialState: DashboardViewsState = {
  data: createEmptyData(),
  profileId: null,
  loadRequestId: null,
  loadStatus: 'idle',
  saveStatus: 'idle',
}

export const getDashboardViewsData = createAsyncThunk<DashboardViewsDto, string>(
  'dashboardViews/get',
  async (_, { rejectWithValue }) => {
    try {
      return await getDashboardViews()
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
)

export const updateDashboardViewsData = createAsyncThunk<DashboardViewsDto, UpdateDashboardViewsPayload>(
  'dashboardViews/update',
  async ({ state }, { rejectWithValue }) => {
    try {
      return await updateDashboardViews(state)
    } catch (error) {
      return rejectWithValue(getErrorMessage(error))
    }
  },
  {
    condition: ({ profileId }, { getState }) => {
      const state = (getState() as { dashboardViews: DashboardViewsState }).dashboardViews
      return state.profileId === profileId && state.saveStatus !== 'loading'
    },
  },
)

const dashboardViewsSlice = createSlice({
  name: 'dashboardViews',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDashboardViewsData.pending, (state, action) => {
        if (state.profileId !== action.meta.arg) {
          state.data = createEmptyData()
          state.saveStatus = 'idle'
        }
        state.profileId = action.meta.arg
        state.loadRequestId = action.meta.requestId
        state.loadStatus = 'loading'
      })
      .addCase(getDashboardViewsData.fulfilled, (state, action) => {
        if (state.loadRequestId !== action.meta.requestId || state.profileId !== action.meta.arg) return

        state.data = action.payload
        state.loadRequestId = null
        state.loadStatus = 'succeeded'
      })
      .addCase(getDashboardViewsData.rejected, (state, action) => {
        if (state.loadRequestId !== action.meta.requestId || state.profileId !== action.meta.arg) return

        state.loadRequestId = null
        state.loadStatus = 'failed'
      })
      .addCase(updateDashboardViewsData.pending, (state, action) => {
        if (state.profileId === action.meta.arg.profileId) state.saveStatus = 'loading'
      })
      .addCase(updateDashboardViewsData.fulfilled, (state, action) => {
        if (state.profileId !== action.meta.arg.profileId) return

        state.data = action.payload
        state.saveStatus = 'succeeded'
      })
      .addCase(updateDashboardViewsData.rejected, (state, action) => {
        if (state.profileId === action.meta.arg.profileId) state.saveStatus = 'failed'
      })
      .addCase(resetStore, () => initialState)
  },
})

export default dashboardViewsSlice.reducer
